import type { XAXisComponentOption, YAXisComponentOption } from "echarts";
import type { BarSeriesOption, CustomSeriesOption, LineSeriesOption } from "echarts/charts";
import type {
    AxisPointerComponentOption,
    GridComponentOption,
    TitleComponentOption,
    TooltipComponentOption,
} from "echarts/components";
import type { ComposeOption } from "echarts/core";
import type { TimeEvent } from "../band-data/types";
import { AxisSide, type AxisSpec, type TimeAxisPanelSpec } from "../ChartSpec";
import { formatReadingValue } from "../format";
import { buildBandAxes, buildBandGrid, buildBandSeries } from "./band/build-band-options";
import { buildHighlightSeries } from "./band/build-highlight-options";
import { type AxisScale, formatTickLabel } from "./layout/axis-layout";
import {
    AXIS_LABEL_COLOR,
    BAR_SERIES_Z,
    CHART_FONT_FAMILY,
    CROSSHAIR_HOVER_COLOR,
    DIMMED_BAR_COLOR,
    DIMMED_LINE_COLOR,
    DIMMED_LINE_WIDTH_PX,
    FOCUSED_SERIES_Z,
    GRID_LINE_COLOR,
    LINE_SERIES_Z,
    PLOT_BASELINE_COLOR,
    PLOT_X_AXIS_ID,
    SERIES_STROKE_WIDTH_PX,
    X_AXIS_TICK_LABEL_FONT_SIZE_PX,
    X_AXIS_TICK_LABEL_LINE_HEIGHT_PX,
    X_AXIS_TICK_LABEL_MARGIN_PX,
    Y_AXIS_BASE_OFFSET_PX,
    Y_TICK_FONT_SIZE_PX,
} from "./layout/plot-geometry";
import type { PlotLayout } from "./layout/plot-layout";
import type { PreparedSeries } from "./prepare-series";

type ECOption = ComposeOption<
    | LineSeriesOption
    | BarSeriesOption
    | CustomSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | GridComponentOption
    | AxisPointerComponentOption
>;

type TimePanelOptions = ECOption & {
    grid: GridComponentOption[];
    xAxis: XAXisComponentOption[];
    yAxis: YAXisComponentOption[];
    series: (LineSeriesOption | BarSeriesOption | CustomSeriesOption)[];
};

export type PanelBandOptions = {
    scheduled: TimeEvent[];
    actual: TimeEvent[];
    nowMs: number;
    position: "above" | "below";
};

export type BandHighlight = { scheduled: TimeEvent[]; actual: TimeEvent[] };

/** Min, max and interval come from layout/axis-layout.ts, so ECharts draws the ticks the gutter was measured for. */
const buildYAxes = (
    axes: AxisSpec[],
    axisScales: Map<string, AxisScale>,
    activeLeftAxisId: string | undefined,
    focusedAxisId: string | undefined,
): YAXisComponentOption[] => {
    // All axes share one offset, so only one per side can show tick labels without overlapping.
    // Extra axes stay in the list (series still bind to them) but hide labels. Keep in sync with
    // visibleAxesForSide in plot-layout.ts.
    const firstRightAxisId = axes.find((axis) => axis.side === AxisSide.Right)?.id;

    // Only one axis draws gridlines; left and right ticks don't line up and would cross-hatch.
    const leftAxisIds = axes.filter((axis) => axis.side === AxisSide.Left).map((axis) => axis.id);
    const gridlineAxisId =
        focusedAxisId ?? leftAxisIds.find((id) => id === activeLeftAxisId) ?? leftAxisIds[0] ?? firstRightAxisId;

    const showsTickLabels = (axis: AxisSpec): boolean => {
        // The header names only the focused axis. The other side keeps its gutter, so hiding it doesn't move the plot.
        if (focusedAxisId !== undefined) return axis.id === focusedAxisId;
        return axis.side === AxisSide.Left ? axis.id === activeLeftAxisId : axis.id === firstRightAxisId;
    };

    return axes.map((axis) => {
        const isVisible = showsTickLabels(axis);
        const scale = axisScales.get(axis.id);

        return {
            id: axis.id,
            type: "value",
            position: axis.side,
            offset: Y_AXIS_BASE_OFFSET_PX,
            // No scale entry means this axis has no data. Omit min/max/interval and let ECharts
            // auto-range: nothing is plotted, so exact ticks don't matter.
            ...(scale ? { min: scale.niceMin, max: scale.niceMax, interval: scale.step } : {}),
            axisLabel: {
                show: isVisible,
                color: AXIS_LABEL_COLOR,
                // fontWeight/fontSize must stay in sync with Y_TICK_FONT in ./layout/plot-geometry.ts —
                // the gutter width was measured with that font string.
                fontWeight: 400,
                fontSize: Y_TICK_FONT_SIZE_PX,
                hideOverlap: true,
                margin: 0,
                formatter: (value: number) => {
                    if (!scale) return formatReadingValue(value);
                    return value <= scale.maxVisibleTick ? formatTickLabel(value, scale.step) : "";
                },
            },
            splitLine: {
                show: axis.id === gridlineAxisId,
                lineStyle: { color: GRID_LINE_COLOR },
            },
        };
    });
};

export const buildTimePanelOptions = (
    spec: TimeAxisPanelSpec,
    series: PreparedSeries[],
    layout: PlotLayout,
    axisScales: Map<string, AxisScale>,
    window: { startMs: number; endMs: number },
    activeLeftAxisId: string | undefined,
    band?: PanelBandOptions,
    highlight?: BandHighlight,
    focusedStreamId?: number,
): TimePanelOptions => {
    // Ids let replaceMerge keep the plot's grid and axis and drop only the band's when it goes away.
    const plotGrid: GridComponentOption = {
        id: "plot",
        // The baseline is the x-axis line.
        show: false,
        // We set exact pixel gutters from the shared width-group grant; containLabel would
        // re-fit the grid around whatever labels ECharts measures and fight those numbers.
        containLabel: false,
        // ECharts 6 shrinks a grid whose axis labels would leave the canvas; the gutters already size for them.
        outerBoundsMode: "none",
        top: layout.plotTopPx,
        left: layout.gutters.left,
        right: layout.gutters.right,
        bottom: layout.plotBottomInsetPx,
    };

    const plotXAxis: XAXisComponentOption = {
        id: PLOT_X_AXIS_ID,
        type: "time",
        // The x-axis should show the user's desired time range - not whatever the actual set of
        // downloaded data starts and ends at.
        min: window.startMs,
        max: window.endMs,
        // ECharts 6 widens a time axis by half a bar at each end to fit bar series, past min/max.
        // The band's axis has no bars, so the linked crosshairs would land on different pixels.
        containShape: false,
        tooltip: {
            show: false,
        },
        // onZero off so the baseline stays at the bottom when a y-axis crosses zero.
        axisLine: {
            show: true,
            onZero: false,
            lineStyle: { color: PLOT_BASELINE_COLOR, width: 1 },
        },
        // The time pill on the divider names the hovered time, so the axis draws none.
        // Off here, not on tooltip.axisPointer, which ignores it; else echarts draws hollow markers on lines.
        axisPointer: { label: { show: false }, triggerEmphasis: false },
        axisLabel: {
            color: AXIS_LABEL_COLOR,
            fontSize: X_AXIS_TICK_LABEL_FONT_SIZE_PX,
            lineHeight: X_AXIS_TICK_LABEL_LINE_HEIGHT_PX,
            // Measured from the plot's bottom, so a band below pushes the labels down past itself.
            margin: X_AXIS_TICK_LABEL_MARGIN_PX + layout.plotBottomInsetPx - layout.xAxisBandPx,
            showMaxLabel: false,
            showMinLabel: false,
            hideOverlap: true,

            formatter: {
                year: "{yyyy}",
                month: "{MMMM}",
                day: "{MMM} {d}",
                hour: "{h}:{mm}{a}",
                minute: "{h}:{mm}{a}",
            },
        },
        axisTick: {
            show: false,
        },
        splitLine: {
            show: true,
            showMinLine: false,
            showMaxLine: false,
            lineStyle: {
                color: GRID_LINE_COLOR,
            },
        },
    };

    const grid = [plotGrid];
    const xAxis: XAXisComponentOption[] = [plotXAxis];
    const focusedAxisId = spec.streams.find((stream) => stream.streamId === focusedStreamId)?.axisId;
    const yAxis = buildYAxes(spec.axes, axisScales, activeLeftAxisId, focusedAxisId);

    // Bars first: ECharts draws series bottom-to-top, so a bar drawn after a line would cover it.
    const allSeries: TimePanelOptions["series"] = [
        ...series.filter((s) => s.seriesType === "bar").map((s) => buildSeriesOption(s, spec, focusedStreamId)),
        ...series.filter((s) => s.seriesType === "line").map((s) => buildSeriesOption(s, spec, focusedStreamId)),
    ];
    let axisPointer: AxisPointerComponentOption | undefined;

    if (band) {
        const plotYAxisId = spec.axes[0]?.id;
        if (highlight && plotYAxisId !== undefined) {
            allSeries.unshift(
                ...buildHighlightSeries(highlight.scheduled, highlight.actual, window, band.nowMs, plotYAxisId),
            );
        }

        const bandAxes = buildBandAxes(window);
        grid.push(buildBandGrid(layout, band.position));
        xAxis.push(bandAxes.xAxis);
        yAxis.push(bandAxes.yAxis);
        allSeries.push(...buildBandSeries(band.scheduled, band.actual, window, band.nowMs));
        axisPointer = { link: [{ xAxisIndex: "all" }] };
    }

    return {
        // Off so the grid snaps when switching metrics changes the gutter, instead of sliding.
        animation: false,
        textStyle: { fontFamily: CHART_FONT_FAMILY },
        grid,
        // The legend is the readout, so the box stays off. showContent, not show: show:false would
        // take the crosshair with it.
        tooltip: {
            showContent: false,
            trigger: "axis",

            // The dashed line that follows the cursor.
            axisPointer: {
                type: "line",
                lineStyle: { color: CROSSHAIR_HOVER_COLOR, width: 1, type: [3, 3] },
            },
        },
        xAxis,
        yAxis,
        series: allSeries,
        axisPointer,
    };
};

/** Where the chart draws a reading: a bar at its bucket's centre, a line at its own time. */
export function drawnTimeMs(series: Pick<PreparedSeries, "seriesType" | "bucketMs">, readingMs: number): number {
    return series.seriesType === "bar" ? readingMs + series.bucketMs / 2 : readingMs;
}

function buildSeriesOption(
    s: PreparedSeries,
    spec: TimeAxisPanelSpec,
    focusedStreamId: number | undefined,
): LineSeriesOption | BarSeriesOption {
    const entry = spec.streams.find((stream) => stream.streamId === s.streamId);
    const axis = spec.axes.find((a) => a.id === entry?.axisId);
    const isFocused = focusedStreamId !== undefined && s.streamId === focusedStreamId;
    const isDimmed = focusedStreamId !== undefined && !isFocused;
    // Always sent: setOption merges, so a series that lost focus would otherwise keep its z 4 and stay on top.
    const defaultZ = s.seriesType === "bar" ? BAR_SERIES_Z : LINE_SERIES_Z;
    const z = isFocused ? FOCUSED_SERIES_Z : defaultZ;

    const base = {
        id: String(s.streamId),
        yAxisId: axis?.id,
        data: s.points,
        color: s.color,
        // HoverValues marks every series; echarts' emphasis marks only the nearest.
        emphasis: { disabled: true },
        z,
    };

    // A bucket is labelled by its start and echarts centres a bar on its x value, so half a bucket
    // forward makes the bar span the interval it sums.
    if (s.seriesType === "bar") {
        return {
            ...base,
            type: "bar",
            data: s.points.map(([time, value]) => [new Date(drawnTimeMs(s, time.getTime())), value]),
            ...(isDimmed ? { color: DIMMED_BAR_COLOR } : {}),
        };
    }

    return {
        ...base,
        type: "line",
        showSymbol: false,
        connectNulls: false,
        ...(isDimmed
            ? { color: DIMMED_LINE_COLOR, lineStyle: { width: DIMMED_LINE_WIDTH_PX } }
            : { lineStyle: { width: SERIES_STROKE_WIDTH_PX } }),
    };
}
